//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import Text from '@components/atoms/text/Text';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { FacebookPageSuggestion } from '@components/molecules/smart-input/mentions/FacebookPageSuggestion';
import { TwitterUserSuggestion } from '@components/molecules/smart-input/mentions/TwitterUserSuggestion';
import { insertMentionAtPosition } from '@components/molecules/smart-input/mentions/mentionsUtils';
import {
  IMentionsSuggestionsQuery,
  MentionsSuggestion,
  MentionsSuggestionType,
} from '@components/molecules/smart-input/smartInputModel';
import { insertText } from '@components/molecules/smart-input/smartInputUtils';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

interface Props {
  query: IMentionsSuggestionsQuery;
}

export const MentionsSuggestionsDropdown = ({ query }: Props) => {
  const smartInputContext = useSmartInputContext();
  const [hasQueried, setHasQueried] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionsSuggestion[]>([]);

  const debouncedGetMentionsSuggetions = useDebouncedCallback(
    async (query: string) => {
      if (query === '') {
        return;
      }

      setIsLoadingSuggestions(true);
      setHasQueried(true);

      const suggestions = smartInputContext.mentions?.getMentionsSuggestions
        ? (await smartInputContext.mentions.getMentionsSuggestions(
            query.toLowerCase(),
          )) ?? []
        : [];

      setSuggestions(suggestions);
      setIsLoadingSuggestions(false);
    },
    500,
  );

  // Handle getting the suggestions in a debounced fashion when the query changes
  useEffect(() => {
    void debouncedGetMentionsSuggetions(query.queryString);
  }, [debouncedGetMentionsSuggetions, query]);

  const handleSuggestionClick = async (mention: MentionsSuggestion) => {
    const { blockKey, end, start } = query;

    let mentionText = '';
    switch (mention.type) {
      case MentionsSuggestionType.FacebookPage:
        mentionText = `@[${mention.id}]`;
        break;
      case MentionsSuggestionType.TwitterUser:
        mentionText = `@${mention.username} `;
        break;
    }

    if (blockKey && isDefined(end) && isDefined(start)) {
      await insertMentionAtPosition(mentionText, smartInputContext, {
        blockKey,
        end,
        start,
      });
    } else {
      // This case handles for if the user has deleted their
      //query (we are no longer replacing a partial mention)
      insertText(mentionText, smartInputContext);
    }

    // This pushes closing the dropdown to the next tick so
    // that focusing the editor while closing doesn't change position
    setTimeout(() => {
      smartInputContext.mentions?.setIsDropdownOpen(false, true);
    }, 0);
  };

  const mentionSuggestions = suggestions.map((mention, idx) => {
    switch (mention.type) {
      case MentionsSuggestionType.TwitterUser:
        return (
          <TwitterUserSuggestion
            key={`${mention.username}-${idx}`}
            handleSuggestionClick={handleSuggestionClick}
            suggestion={mention}
          />
        );
      case MentionsSuggestionType.FacebookPage:
        return (
          <FacebookPageSuggestion
            key={`${mention.id}-${idx}`}
            handleSuggestionClick={handleSuggestionClick}
            suggestion={mention}
          />
        );
      default:
        return null;
    }
  });

  const fallbackContent = (() => {
    if (!query.queryString) {
      return smartInputContext.mentions?.getFacebookPageById ? (
        <EmptyText>Start typing to tag pages.</EmptyText>
      ) : (
        <EmptyText>Start typing to tag usernames.</EmptyText>
      );
    }

    if (isLoadingSuggestions) {
      return <CenteredSpinner />;
    }

    if (!mentionSuggestions.length && hasQueried) {
      return <EmptyText>No results match your search.</EmptyText>;
    }

    return null;
  })();

  return (
    <DropdownWrapper tabIndex={-1}>
      {mentionSuggestions.length ? mentionSuggestions : null}
      <FallbackContentWrapper>{fallbackContent}</FallbackContentWrapper>
    </DropdownWrapper>
  );
};

const DropdownWrapper = styled.div(
  ({ theme }) => `
  ${Mixins.Sheet.High}
  flex-direction: column;
  position: absolute;
  margin: 2px 0 0;
  padding: ${theme.spacings[2]} 0;
  width: 100%;
  
  /**
   * From design: This height accommodates 4 suggestions plus a partial of a
   * 5th suggestion before vertically scrolling.
   */
  max-height: 320px;
  min-height: ${theme.spacings[7]};
  overflow: auto;
  z-index: ${theme.zindex.fixed};

  &:focus {
    outline: none;
  }
  `,
);

const FallbackContentWrapper = styled.div`
  margin: auto;
`;

const EmptyText = styled(Text).attrs({
  mixin: Mixins.Text.UiCopySecondary,
})(
  ({ theme }) => `
  width: 100%;
  padding: ${theme.spacings[2]};
  text-align: center;
  margin: auto;
`,
);
